/* eslint react/display-name: 0 */
import qs from 'qs';
import React from 'react';
import debug from 'debug';
import { render } from 'react-dom';
import { renderToString } from 'react-dom/server';
import { Router, RouterContext } from 'react-router';
import AltContainer from 'alt-container';
import {Provider} from 'react-redux';
import {makeStore} from 'lib/redux/store';
import intlLoader from 'utils/intl-loader';
import InternalServerErrorPage from 'components/core/InternalServerErrorPage';
import sysConfig from '../server/config';

// We just need something unique
const WrapperDomId = '_universalRender-wrapper';

const { BROWSER, NODE_ENV } = process.env;

const toParams = (location) => {
  const queryString = location.split('?')[1];

  return queryString ? qs.parse(queryString) : {};
};

// NOTE: This is disabled by default for performance reasons. Enable when we can fix it.
const shouldRenderServerSide = (location) => toParams(location).rendering === 'server';

// NOTE: This is disabled until we have a way to define data needs per component.
const shouldPrefetchComponentData = (location) => toParams(location).rendering === 'prefetch';

const appendData = (html, serializedData, reduxData) => {
  return `
    <div id="${WrapperDomId}">
      ${html}
    </div>
    <script>var _reduxData = ${reduxData};</script>
    <script>var _franklyInitialData = ${serializedData};</script>
  `;
};

/* eslint space-before-function-paren:0 */
// https://github.com/eslint/eslint/issues/4442
export default async ({ redux, flux, pageUri: location, renderProps, isSSR }) => {
  if (BROWSER) {
    if (NODE_ENV === 'development') {
      require('alt-utils/lib/chromeDebug')(flux);
    }

    // NOTE: flux.bootstrap only takes string
    flux.bootstrap(JSON.stringify(window._franklyInitialData));

    let newRedux = makeStore(window._reduxData);

    // load the intl-polyfill if needed
    // load the correct data/{lang}.json into app
    const { locales: [ locale ] } = flux.getStore('locale').getState();
    const { messages } = await intlLoader(locale);
    flux.getActions('locale').switchLocale({ locale, messages });

    const routes = require('routes');
    const { browserHistory } = require('react-router');

    const element = (
      <Provider store={newRedux} ssr={'client'} >
        <AltContainer flux={ flux }>
          <Router
            history={ browserHistory }
            routes={ routes(flux) } />
        </AltContainer>
      </Provider>
    );

    // `alt-resolver` will respect server setting on the first render
    flux.resolver.didFetchData = shouldRenderServerSide(location) || shouldPrefetchComponentData(location);

    // Render element in the same container as the SSR
    render(element, document.getElementById(WrapperDomId));

    // Tell `alt-resolver` we have done the first render. Next promises will be resolved.
    flux.resolver.didFetchData = false;
  } else {
    let element = (
      <Provider store={redux}>
        <AltContainer flux={ flux }>
          <RouterContext { ...renderProps } />
        </AltContainer>
      </Provider>
    );

    // const routes = require('routes');
    // const { browserHistory } = require('react-router');
    // element = (
    //   <Provider store={redux} ssr={'true'} >
    //     <AltContainer flux={ flux }>
    //       <Router
    //         history={ browserHistory }
    //         routes={ routes(flux) } />
    //     </AltContainer>
    //   </Provider>
    // );

    let app;
    let fluxSnapshot;
    let reduxSnapshot;

    try {
      const shouldRender = shouldRenderServerSide(location);

      if (shouldRender || shouldPrefetchComponentData(location)) {
        // TODO: Figure out/implement a better way to resolve data dependencies
        // Collect promises with a first render
        debug('dev')('first server render');
        renderToString(element);

        await flux.resolver.dispatchPendingActions();
      }

      //if (shouldRender) {
      if (isSSR || shouldRender) {
        debug('dev')('second server render');
        app = renderToString(element);
      } else {
        app = ' ';
      }

      // TODO: Improve performance by taking partial snapshot
      //fluxSnapshot = flux.takeSnapshot();
      fluxSnapshot = flux.takeSnapshot().replace(/</g, '\\u003c');

      try {
        if (sysConfig.ssr.settings.SSR_PASSING_REDUX) {
          reduxSnapshot = JSON.stringify(redux.getState()).replace(/</g, '\\u003c');
        } else {
          reduxSnapshot = `{affiliateZipcode: ${redux.getState().affiliateZipcode}}`; // no passing of redux data to client-side, except affiliateZipcode
        }
      } catch (reduxError) {
        console.log('>>> Redux stringify error:', reduxError);
      }
    } catch (renderErr) {
      // Catch rendering error, render a 500 page
      debug('koa')('rendering error');
      debug('koa')(renderErr);

      fluxSnapshot = flux.takeSnapshot();
      app = renderToString(<InternalServerErrorPage />);
    }

    // Get status code, page title and page metas for rendering
    const { titleBase, title, metas, statusCode } = flux.getStore('Helmet').getState();

    return {
      statusCode,
      metas,
      body: appendData(app, fluxSnapshot, reduxSnapshot),
      title: (title ? (title + ' - ') : '') + titleBase
    };
  }
};



// WEBPACK FOOTER //
// ./shared/universalRender.js